import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Nullable } from "../../../../Types/Common";
import { formattedYMDDate } from "../../../../utils/dateUtils";
import { IGetDailyDairySlots, IGetDailyDairyTaskDetails } from "../../types";

export interface ICopyFormDetails {
  formDetails: Nullable<IGetDailyDairyTaskDetails>;
  showToast: boolean;
}

export interface IActiveInterval {
  start_time: string;
  end_time: string;
  slot_id: number;
  slot_name: string;
  task_details: IGetDailyDairyTaskDetails[];
}

interface IInitialState {
  activeInterval: IActiveInterval;
  dailySlotList: IGetDailyDairySlots[];
  formDetails: Nullable<IGetDailyDairyTaskDetails>;
  isCopy: {
    form: boolean;
    toast: boolean;
  };
  notesAdded: Nullable<number>;
  notesHours: Nullable<number>;
  openAccordionId: Nullable<string>;
  expandAllLongDescriptions: boolean;
  workDairyDate: Nullable<string>;
  workDairyDatePicker: string;
  isSidbebarOpen: boolean;
  dailyDairyStatus: string;
  dailyDairyClockInTime: string;
  dailyDairyClockOutTime: string;
}

const initialState: IInitialState = {
  activeInterval: {
    start_time: "",
    end_time: "",
    slot_id: 0,
    slot_name: "",
    task_details: [],
  },
  dailySlotList: [],
  formDetails: null,
  isCopy: {
    form: false,
    toast: false,
  },
  notesAdded: null,
  notesHours: null,
  openAccordionId: null,
  expandAllLongDescriptions: false,
  workDairyDate: null,
  workDairyDatePicker: formattedYMDDate(new Date()),
  isSidbebarOpen: false,
  dailyDairyStatus: "",
  dailyDairyClockInTime: "",
  dailyDairyClockOutTime: "",
};

const dailyDairySlice = createSlice({
  name: "dailyDairySlice",
  initialState,
  reducers: {
    setActiveInterval: (
      state,
      action: PayloadAction<IGetDailyDairySlots | null>
    ) => {
      state.activeInterval.start_time = action?.payload?.start_time ?? "";
      state.activeInterval.end_time = action?.payload?.end_time ?? "";
      state.activeInterval.slot_id = action?.payload?.slot_id ?? 0;
      state.activeInterval.task_details = action?.payload?.tasks ?? [];
      state.activeInterval.slot_name = action?.payload?.slot_name ?? "";
    },
    setDailySlotList: (state, action: PayloadAction<IGetDailyDairySlots[]>) => {
      state.dailySlotList = action.payload;
    },
    setFormDetails: (
      state,
      action: PayloadAction<Nullable<IGetDailyDairyTaskDetails>>
    ) => {
      state.formDetails = action.payload;
    },
    setIsCopy: (
      state,
      action: PayloadAction<{ form: boolean; toast: boolean }>
    ) => {
      state.isCopy.form = action.payload.form;
      state.isCopy.toast = action.payload.toast;
    },
    setNotesAdded: (state, action: PayloadAction<Nullable<number>>) => {
      state.notesAdded = action.payload;
    },
    setNoteHours: (state, action: PayloadAction<Nullable<number>>) => {
      state.notesHours = action.payload;
    },
    setOpenAccordionId: (state, action: PayloadAction<Nullable<string>>) => {
      state.openAccordionId =
        state.openAccordionId === action.payload ? null : action.payload;
    },
    setExpandAllLongDescriptions: (state, action: PayloadAction<boolean>) => {
      state.expandAllLongDescriptions = action.payload;
    },

    setWorkDairyDate: (state, action: PayloadAction<Nullable<string>>) => {
      state.workDairyDate = action.payload;
    },

    setWorkDairyDatePicker: (state, action: PayloadAction<string>) => {
      state.workDairyDatePicker = action.payload;
    },
    setIsSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.isSidbebarOpen = action.payload;
    },

    setDailyDairyStatus: (state, action: PayloadAction<string>) => {
      state.dailyDairyStatus = action.payload;
    },
    setDailyDairyClockInTime: (state, action: PayloadAction<string>) => {
      state.dailyDairyClockInTime = action.payload;
    },
    setDailyDairyClockOutTime: (state, action: PayloadAction<string>) => {
      state.dailyDairyClockOutTime = action.payload;
    },
  },
});

export const {
  setIsCopy,
  setNoteHours,
  setNotesAdded,
  setFormDetails,
  setWorkDairyDate,
  setDailySlotList,
  setIsSidebarOpen,
  setActiveInterval,
  setOpenAccordionId,
  setDailyDairyStatus,
  setWorkDairyDatePicker,
  setDailyDairyClockInTime,
  setDailyDairyClockOutTime,
  setExpandAllLongDescriptions,
} = dailyDairySlice.actions;

export default dailyDairySlice.reducer;
